import { apiUrl } from './config/api';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { UserAuthContext } from './UserAuthContext';

export const FavoritesContext = createContext({
  favorites: [],
  loading: false,
  isFavorite: () => false,
  isUpdating: () => false,
  toggleFavorite: async () => {},
  refreshFavorites: async () => {},
});

export const FavoritesProvider = ({ children }) => {
  const { user } = useContext(UserAuthContext);
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingIds, setPendingIds] = useState(() => new Set());

  const refreshFavorites = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!user || !token) {
      setFavorites([]);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(apiUrl('/api/watchlist'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Unable to load favorites');
      setFavorites(await response.json());
    } catch (error) {
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refreshFavorites();
  }, [refreshFavorites]);

  const favoriteIds = useMemo(
    () => new Set(favorites.map((product) => String(product.productid || product.id))),
    [favorites]
  );

  const toggleFavorite = useCallback(async (productId) => {
    if (!user) {
      toast.info('Please log in to save favorites.');
      navigate('/login');
      return false;
    }
    const id = String(productId || '').trim();
    if (!/^\d+$/.test(id) || Number(id) < 1) {
      toast.error('This product cannot be added to favorites right now.');
      return false;
    }
    const currentlyFavorite = favoriteIds.has(id);
    if (pendingIds.has(id)) return currentlyFavorite;
    const token = localStorage.getItem('token');
    setPendingIds((current) => new Set(current).add(id));
    try {
      const method = currentlyFavorite ? 'DELETE' : 'POST';
      const response = await fetch(apiUrl(`/api/watchlist/${encodeURIComponent(id)}`), {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(method === 'POST' ? { 'Content-Type': 'application/json' } : {}),
        },
        ...(method === 'POST' ? { body: '{}' } : {}),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Unable to update favorites');
      if (currentlyFavorite) {
        setFavorites((current) => current.filter((product) => String(product.productid || product.id) !== id));
        toast.info('Product Removed from Favorites.');
      } else {
        setFavorites((current) => {
          if (current.some((product) => String(product.productid || product.id) === id)) return current;
          return [result.product || { id, productid: Number(id) }, ...current];
        });
        toast.success('Product Added to Favorites!');
      }
      return !currentlyFavorite;
    } catch (error) {
      toast.error(error.message);
      return currentlyFavorite;
    } finally {
      setPendingIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }
  }, [favoriteIds, navigate, pendingIds, user]);

  return (
    <FavoritesContext.Provider value={{
      favorites,
      loading,
      isFavorite: (productId) => favoriteIds.has(String(productId)),
      isUpdating: (productId) => pendingIds.has(String(productId)),
      toggleFavorite,
      refreshFavorites,
    }}>
      {children}
    </FavoritesContext.Provider>
  );
};
